import type {
  breakdownProcess,
  childPartV2Param,
} from "@/api/breakdownService";
import { TableCell, TableRow, TextField } from "@mui/material";
import ChildPartDataEdit from "./ChildPartDataEdit";
import { isValidDecimalInput, parseDecimal, parseInteger } from "@/utils/inputParse";
import { useEffect, useState } from "react";
import { useCalculateCPProcessCost } from "@/hooks/useBreakdownLogic";

interface ChildPartProcessV2DataEditProps {
  partNo: string;
  childPartProcess: Array<breakdownProcess>;
  newChildPart: childPartV2Param;
  setNewChildPart: React.Dispatch<
    React.SetStateAction<childPartV2Param | null>
  >;
  validationErrors: Set<string>;
  setFlaggedReason: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const ChildPartProcessV2DataEdit = ({
  childPartProcess,
  newChildPart,
  setNewChildPart,
  partNo,
  validationErrors,
  setFlaggedReason,
}: ChildPartProcessV2DataEditProps) => {
  const cpProcesses = childPartProcess || [];
  const processCount = cpProcesses.length > 0 ? cpProcesses.length : 1;
  const [cpProcessInputs, setCpProcessInputs] = useState<{
    [key: number]: {
      qty_1: string;
      cost_per_qty_1: string;
      qty_2: string;
      cost_per_qty_2: string;
    };
  }>({});
  const { cpProcessCost, SumCPProcessCost, totalCPProcessCost } =
    useCalculateCPProcessCost(newChildPart, childPartProcess);

  useEffect(() => {
    if (newChildPart?.child_part_processes_v2) {
      const inputs = newChildPart.child_part_processes_v2.reduce(
        (acc, process) => {
          acc[process.part_process_v2_id] = {
            qty_1: Number(process.qty_1 ?? 0).toFixed(3),
            cost_per_qty_1: Number(process.cost_per_qty_1 ?? 0).toFixed(3),
            qty_2: Number(process.qty_2 ?? 0).toFixed(3),
            cost_per_qty_2: Number(process.cost_per_qty_2 ?? 0).toFixed(3),
          };
          return acc;
        },
        {} as {
          [key: number]: {
            qty_1: string;
            cost_per_qty_1: string;
            qty_2: string;
            cost_per_qty_2: string;
          };
        },
      );
      setCpProcessInputs(inputs);
    }
  }, [newChildPart?.child_part_processes_v2]);

  if (cpProcesses.length === 0) {
    return (
      <TableRow
        sx={{
          backgroundColor: "#fffbe6",
        }}
      >
        <ChildPartDataEdit
          newChildPart={newChildPart}
          setNewChildPart={setNewChildPart}
          processCount={1}
          partNo={partNo}
          validationErrors={validationErrors}
        />
        <TableCell
          sx={{
            minWidth: 180,
            maxWidth: 180,
            borderRight: "1px solid #e0e0e0",
            borderLeft: "1px solid #e0e0e0",
            textAlign: "right",
            fontSize: "0.9rem",
          }}
        >
          {0}
        </TableCell>
        <TableCell
          sx={{
            color: "#888888",
            borderRight: "1px solid #e0e0e0",
            borderLeft: "1px solid #e0e0e0",
            textAlign: "left",
            fontSize: "0.9rem",
          }}
          colSpan={11}
        >
          No Process
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {cpProcesses.map((process, index) => (
        <TableRow
          key={index}
          sx={{
            backgroundColor: "#fffbe6",
          }}
        >
          {index === 0 && (
            <>
              <ChildPartDataEdit
                newChildPart={newChildPart}
                setNewChildPart={setNewChildPart}
                processCount={processCount}
                partNo={partNo}
                validationErrors={validationErrors}
              />
              <TableCell
                sx={{
                  minWidth: 180,
                  maxWidth: 180,
                  borderRight: "1px solid #e0e0e0",
                  borderLeft: "1px solid #e0e0e0",
                  textAlign: "right",
                  fontSize: "0.9rem",
                }}
                rowSpan={processCount}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ "& input": { textAlign: "right" } }}
                  value={Number(totalCPProcessCost ?? 0).toFixed(2)}
                  disabled
                />
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 180,
                  maxWidth: 180,
                  borderRight: "1px solid #e0e0e0",
                  borderLeft: "1px solid #e0e0e0",
                  textAlign: "right",
                  fontSize: "0.9rem",
                }}
                rowSpan={processCount}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ "& input": { textAlign: "right" } }}
                  value={Number(SumCPProcessCost ?? 0).toFixed(2)}
                  disabled
                />
              </TableCell>
            </>
          )}
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              sx={{ "& input": { textAlign: "right" } }}
              value={Number(
                cpProcessCost?.find((p) => p.id === process.id)?.cost ?? 0,
              ).toFixed(2)}
              disabled
            />
          </TableCell>
          <TableCell
            sx={{
              minWidth: 200,
              maxWidth: 200,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "left",
              fontSize: "0.9rem",
            }}
          >
            {process.name}
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "left",
              fontSize: "0.9rem",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              sx={{ "& input": { textAlign: "left" } }}
              value={
                newChildPart?.child_part_processes_v2?.find(
                  (p) => p.part_process_v2_id === process.id,
                )?.detail || ""
              }
              onChange={(e) => {
                setNewChildPart((prev) => {
                  if (!prev) return null;
                  const existingProcess = prev.child_part_processes_v2?.find(
                    (p) => p.part_process_v2_id === process.id,
                  );
                  const otherProcesses =
                    prev.child_part_processes_v2?.filter(
                      (p) => p.part_process_v2_id !== process.id,
                    ) || [];
                  return {
                    ...prev,
                    child_part_processes_v2: [
                      ...otherProcesses,
                      {
                        part_process_v2_id: process.id,
                        detail: e.target.value,
                        qty_1: existingProcess?.qty_1 ?? 0,
                        cost_per_qty_1: existingProcess?.cost_per_qty_1 ?? 0,
                        qty_2: existingProcess?.qty_2 ?? 0,
                        cost_per_qty_2: existingProcess?.cost_per_qty_2 ?? 0,
                        tooling_name: existingProcess?.tooling_name ?? "",
                        tooling_cost: existingProcess?.tooling_cost ?? 0,
                      },
                    ],
                  };
                });
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              sx={{ "& input": { textAlign: "right" } }}
              value={cpProcessInputs[process.id]?.qty_1 ?? "0.000"}
              onChange={(e) => {
                const val = e.target.value;
                if (isValidDecimalInput(val, 3, false)) {
                  setCpProcessInputs((prev) => ({
                    ...prev,
                    [process.id]: {
                      ...prev[process.id],
                      qty_1: val.trim(),
                    },
                  }));
                }
              }}
              onBlur={(e) => {
                const newQty1 = parseDecimal(e.target.value, 3);
                setCpProcessInputs((prev) => ({
                  ...prev,
                  [process.id]: {
                    ...prev[process.id],
                    qty_1: newQty1.toFixed(3),
                  },
                }));
                setNewChildPart((prev) => {
                  if (!prev) return null;
                  const existingProcess = prev.child_part_processes_v2?.find(
                    (p) => p.part_process_v2_id === process.id,
                  );
                  const otherProcesses =
                    prev.child_part_processes_v2?.filter(
                      (p) => p.part_process_v2_id !== process.id,
                    ) || [];
                  return {
                    ...prev,
                    child_part_processes_v2: [
                      ...otherProcesses,
                      {
                        part_process_v2_id: process.id,
                        detail: existingProcess?.detail || "",
                        qty_1: newQty1,
                        cost_per_qty_1: existingProcess?.cost_per_qty_1 ?? 0,
                        qty_2: existingProcess?.qty_2 ?? 0,
                        cost_per_qty_2: existingProcess?.cost_per_qty_2 ?? 0,
                        tooling_name: existingProcess?.tooling_name ?? "",
                        tooling_cost: existingProcess?.tooling_cost ?? 0,
                      },
                    ],
                  };
                });
                setFlaggedReason((prev) => {
                  const newSet = new Set(prev);
                  newSet.add("Child Part Process Value");
                  return newSet;
                });
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              sx={{ "& input": { textAlign: "right" } }}
              value={cpProcessInputs[process.id]?.cost_per_qty_1 ?? "0.000"}
              onChange={(e) => {
                const val = e.target.value;
                if (isValidDecimalInput(val, 3)) {
                  setCpProcessInputs((prev) => ({
                    ...prev,
                    [process.id]: {
                      ...prev[process.id],
                      cost_per_qty_1: val.trim(),
                    },
                  }));
                }
              }}
              onBlur={(e) => {
                const newCostPerQty1 = parseDecimal(e.target.value, 3);
                setCpProcessInputs((prev) => ({
                  ...prev,
                  [process.id]: {
                    ...prev[process.id],
                    cost_per_qty_1: newCostPerQty1.toFixed(3),
                  },
                }));
                setNewChildPart((prev) => {
                  if (!prev) return null;
                  const existingProcess = prev.child_part_processes_v2?.find(
                    (p) => p.part_process_v2_id === process.id,
                  );
                  const otherProcesses =
                    prev.child_part_processes_v2?.filter(
                      (p) => p.part_process_v2_id !== process.id,
                    ) || [];
                  return {
                    ...prev,
                    child_part_processes_v2: [
                      ...otherProcesses,
                      {
                        part_process_v2_id: process.id,
                        detail: existingProcess?.detail || "",
                        qty_1: existingProcess?.qty_1 ?? 0,
                        cost_per_qty_1: newCostPerQty1,
                        qty_2: existingProcess?.qty_2 ?? 0,
                        cost_per_qty_2: existingProcess?.cost_per_qty_2 ?? 0,
                        tooling_name: existingProcess?.tooling_name ?? "",
                        tooling_cost: existingProcess?.tooling_cost ?? 0,
                      },
                    ],
                  };
                });
                setFlaggedReason((prev) => {
                  const newSet = new Set(prev);
                  newSet.add("Child Part Process Value");
                  return newSet;
                });
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              sx={{ "& input": { textAlign: "right" } }}
              value={cpProcessInputs[process.id]?.qty_2 ?? "0.000"}
              onChange={(e) => {
                const val = e.target.value;
                if (isValidDecimalInput(val, 3, false)) {
                  setCpProcessInputs((prev) => ({
                    ...prev,
                    [process.id]: {
                      ...prev[process.id],
                      qty_2: val.trim(),
                    },
                  }));
                }
              }}
              onBlur={(e) => {
                const newQty2 = parseDecimal(e.target.value, 3);
                setCpProcessInputs((prev) => ({
                  ...prev,
                  [process.id]: {
                    ...prev[process.id],
                    qty_2: newQty2.toFixed(3),
                  },
                }));
                setNewChildPart((prev) => {
                  if (!prev) return null;
                  const existingProcess = prev.child_part_processes_v2?.find(
                    (p) => p.part_process_v2_id === process.id,
                  );
                  const otherProcesses =
                    prev.child_part_processes_v2?.filter(
                      (p) => p.part_process_v2_id !== process.id,
                    ) || [];
                  return {
                    ...prev,
                    child_part_processes_v2: [
                      ...otherProcesses,
                      {
                        part_process_v2_id: process.id,
                        detail: existingProcess?.detail || "",
                        qty_1: existingProcess?.qty_1 ?? 0,
                        cost_per_qty_1: existingProcess?.cost_per_qty_1 ?? 0,
                        qty_2: newQty2,
                        cost_per_qty_2: existingProcess?.cost_per_qty_2 ?? 0,
                        tooling_name: existingProcess?.tooling_name ?? "",
                        tooling_cost: existingProcess?.tooling_cost ?? 0,
                      },
                    ],
                  };
                });
                setFlaggedReason((prev) => {
                  const newSet = new Set(prev);
                  newSet.add("Child Part Process Value");
                  return newSet;
                });
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              sx={{ "& input": { textAlign: "right" } }}
              value={cpProcessInputs[process.id]?.cost_per_qty_2 ?? "0.000"}
              onChange={(e) => {
                const val = e.target.value;
                if (isValidDecimalInput(val, 3)) {
                  setCpProcessInputs((prev) => ({
                    ...prev,
                    [process.id]: {
                      ...prev[process.id],
                      cost_per_qty_2: val.trim(),
                    },
                  }));
                }
              }}
              onBlur={(e) => {
                const newCostPerQty2 = parseDecimal(e.target.value, 3);
                setCpProcessInputs((prev) => ({
                  ...prev,
                  [process.id]: {
                    ...prev[process.id],
                    cost_per_qty_2: newCostPerQty2.toFixed(3),
                  },
                }));
                setNewChildPart((prev) => {
                  if (!prev) return null;
                  const existingProcess = prev.child_part_processes_v2?.find(
                    (p) => p.part_process_v2_id === process.id,
                  );
                  const otherProcesses =
                    prev.child_part_processes_v2?.filter(
                      (p) => p.part_process_v2_id !== process.id,
                    ) || [];
                  return {
                    ...prev,
                    child_part_processes_v2: [
                      ...otherProcesses,
                      {
                        part_process_v2_id: process.id,
                        detail: existingProcess?.detail || "",
                        qty_1: existingProcess?.qty_1 ?? 0,
                        cost_per_qty_1: existingProcess?.cost_per_qty_1 ?? 0,
                        qty_2: existingProcess?.qty_2 ?? 0,
                        cost_per_qty_2: newCostPerQty2,
                        tooling_name: existingProcess?.tooling_name ?? "",
                        tooling_cost: existingProcess?.tooling_cost ?? 0,
                      },
                    ],
                  };
                });
                setFlaggedReason((prev) => {
                  const newSet = new Set(prev);
                  newSet.add("Child Part Process Value");
                  return newSet;
                });
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "left",
              fontSize: "0.9rem",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              sx={{ "& input": { textAlign: "left" } }}
              value={
                newChildPart?.child_part_processes_v2?.find(
                  (p) => p.part_process_v2_id === process.id,
                )?.tooling_name || ""
              }
              onChange={(e) => {
                setNewChildPart((prev) => {
                  if (!prev) return null;
                  const existingProcess = prev.child_part_processes_v2?.find(
                    (p) => p.part_process_v2_id === process.id,
                  );
                  const otherProcesses =
                    prev.child_part_processes_v2?.filter(
                      (p) => p.part_process_v2_id !== process.id,
                    ) || [];
                  return {
                    ...prev,
                    child_part_processes_v2: [
                      ...otherProcesses,
                      {
                        part_process_v2_id: process.id,
                        detail: existingProcess?.detail || "",
                        qty_1: existingProcess?.qty_1 ?? 0,
                        cost_per_qty_1: existingProcess?.cost_per_qty_1 ?? 0,
                        qty_2: existingProcess?.qty_2 ?? 0,
                        cost_per_qty_2: existingProcess?.cost_per_qty_2 ?? 0,
                        tooling_name: e.target.value,
                        tooling_cost: existingProcess?.tooling_cost ?? 0,
                      },
                    ],
                  };
                });
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              minWidth: 120,
              maxWidth: 120,
              borderRight: "1px solid #e0e0e0",
              borderLeft: "1px solid #e0e0e0",
              textAlign: "right",
              fontSize: "0.9rem",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              sx={{ "& input": { textAlign: "right" } }}
              value={
                newChildPart?.child_part_processes_v2?.find(
                  (p) => p.part_process_v2_id === process.id,
                )?.tooling_cost || ""
              }
              onChange={(e) => {
                setNewChildPart((prev) => {
                  if (!prev) return null;
                  const existingProcess = prev.child_part_processes_v2?.find(
                    (p) => p.part_process_v2_id === process.id,
                  );
                  const otherProcesses =
                    prev.child_part_processes_v2?.filter(
                      (p) => p.part_process_v2_id !== process.id,
                    ) || [];
                  return {
                    ...prev,
                    child_part_processes_v2: [
                      ...otherProcesses,
                      {
                        part_process_v2_id: process.id,
                        detail: existingProcess?.detail || "",
                        qty_1: existingProcess?.qty_1 ?? 0,
                        cost_per_qty_1: existingProcess?.cost_per_qty_1 ?? 0,
                        qty_2: existingProcess?.qty_2 ?? 0,
                        cost_per_qty_2: existingProcess?.cost_per_qty_2 ?? 0,
                        tooling_name: existingProcess?.tooling_name || "",
                        tooling_cost: parseInteger(e.target.value),
                      },
                    ],
                  };
                });
              }}
            />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

export default ChildPartProcessV2DataEdit;
